/*
  Product page behaviour: variant selection, gallery, zoom, sticky add to cart,
  lazy related products. Loaded only by the product section.
*/
(() => {
  'use strict';

  const Theme = window.Theme || {};
  const money = (cents) => Theme.formatMoney(cents);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      const inventoryEl = this.querySelector('[data-inventory]');
      this.inventory = inventoryEl ? JSON.parse(inventoryEl.textContent) : {};
      this.fieldsets = [...this.querySelectorAll('fieldset')];
      this.addEventListener('change', () => this.onChange());
      this.markAvailability();
    }

    selected() {
      return this.fieldsets.map((fs) => fs.querySelector('input:checked')?.value ?? null);
    }

    onChange() {
      const selected = this.selected();
      this.fieldsets.forEach((fs, i) => {
        const label = fs.querySelector('[data-option-value]');
        if (label) label.textContent = selected[i] ?? '';
      });
      const variant = this.variants.find((v) => v.options.every((opt, i) => opt === selected[i])) || null;
      this.markAvailability();
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    // A value is available if some available variant matches it together with the
    // values already chosen in the options before it (same rule as Shopify's own themes).
    markAvailability() {
      const selected = this.selected();
      this.fieldsets.forEach((fs, index) => {
        fs.querySelectorAll('input').forEach((input) => {
          const ok = this.variants.some(
            (v) =>
              v.available &&
              v.options[index] === input.value &&
              v.options.slice(0, index).every((opt, j) => opt === selected[j])
          );
          input.nextElementSibling?.classList.toggle('is-unavailable', !ok);
        });
      });
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Product section wiring ---------- */
  function initProduct(section) {
    const strings = JSON.parse(section.querySelector('[data-product-strings]')?.textContent || '{}');
    const lowStock = Number(section.dataset.lowStock || 5);
    const picker = section.querySelector('variant-picker');
    const idInput = section.querySelector('[data-variant-id]');
    const priceEl = section.querySelector('[data-price-wrap] .price');
    const stockEl = section.querySelector('[data-stock]');
    const skuEl = section.querySelector('[data-sku]');
    const atc = section.querySelector('[data-atc]');
    const atcLabel = section.querySelector('[data-atc-label]');
    const stickyBtn = section.querySelector('[data-sticky-btn]');
    const stickyPrice = section.querySelector('[data-sticky-price]');
    const gallery = section.querySelector('media-gallery');

    const renderPrice = (v) => {
      if (!priceEl) return;
      const onSale = v.compare_at_price && v.compare_at_price > v.price;
      priceEl.classList.toggle('price--sale', Boolean(onSale));
      let html = `<span class="price__current">${money(v.price)}</span>`;
      if (onSale) {
        html += `<s class="price__compare"><span class="visually-hidden">${strings.regularPrice || ''}</span>${money(
          v.compare_at_price
        )}</s>`;
        html += `<span class="price__save">${(strings.youSave || '').replace('[amount]', money(v.compare_at_price - v.price))}</span>`;
      }
      priceEl.innerHTML = html;
    };

    const renderStock = (v) => {
      if (!stockEl) return;
      const qty = picker ? picker.inventory[v.id] : null;
      let cls = 'stock--in';
      let text = strings.inStock;
      if (!v.available) {
        cls = 'stock--out';
        text = strings.soldOut;
      } else if (typeof qty === 'number' && qty > 0 && qty <= lowStock) {
        cls = 'stock--low';
        text = (strings.onlyLeft || '').replace('[count]', qty);
      }
      stockEl.innerHTML = `<p class="stock ${cls}">${text}</p>`;
    };

    const setButtons = (enabled, label) => {
      [atc, stickyBtn].forEach((btn) => {
        if (btn) btn.disabled = !enabled;
      });
      if (atcLabel) atcLabel.textContent = label;
      if (stickyBtn) stickyBtn.textContent = label;
    };

    section.addEventListener('variant:change', (event) => {
      const v = event.detail.variant;
      if (!v) {
        setButtons(false, strings.unavailable);
        if (stockEl) stockEl.innerHTML = `<p class="stock stock--out">${strings.unavailable}</p>`;
        return;
      }
      if (idInput) idInput.value = v.id;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState(window.history.state, '', url.toString());

      renderPrice(v);
      renderStock(v);
      if (skuEl) skuEl.textContent = v.sku || '';
      if (stickyPrice) stickyPrice.textContent = money(v.price);
      setButtons(v.available, v.available ? strings.addToCart : strings.soldOut);
      if (v.featured_media && gallery) gallery.showMedia(v.featured_media.id);
    });
  }

  /* ---------- Media gallery ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.dialog = this.querySelector('[data-zoom-dialog]');

      this.thumbs.forEach((btn) => btn.addEventListener('click', () => this.showMedia(btn.dataset.thumb)));

      if (this.track && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.setActive(entry.target.dataset.mediaId);
            });
          },
          { root: this.track, threshold: 0.6 }
        );
        this.track.querySelectorAll('[data-media-id]').forEach((slide) => observer.observe(slide));
      }

      this.addEventListener('click', (event) => {
        const zoom = event.target.closest('[data-zoom]');
        if (zoom && this.dialog) this.openZoom(zoom.dataset.zoom, zoom.querySelector('img')?.alt || '');
      });
      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog || event.target.closest('[data-zoom-close]')) this.dialog.close();
      });
    }

    showMedia(id) {
      const slide = this.track?.querySelector(`[data-media-id="${id}"]`);
      if (!slide) return;
      slide.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
      this.setActive(String(id));
    }

    setActive(id) {
      this.thumbs.forEach((btn) => {
        const active = btn.dataset.thumb === String(id);
        btn.classList.toggle('is-active', active);
        if (active) btn.setAttribute('aria-current', 'true');
        else btn.removeAttribute('aria-current');
      });
    }

    openZoom(src, alt) {
      const img = this.dialog.querySelector('[data-zoom-image]');
      img.src = src;
      img.alt = alt;
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ---------- Sticky add to cart (mobile) ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const form = document.getElementById(this.dataset.form);
      const target = form?.querySelector('[data-atc]');
      if (!target || !('IntersectionObserver' in window)) return;
      const root = document.documentElement;
      new IntersectionObserver(([entry]) => {
        // Show whenever the main button is off screen (above OR below), so on phones the
        // buy button is reachable from the first screen of the product page.
        const show = !entry.isIntersecting;
        this.hidden = !show;
        root.style.setProperty('--sticky-bar-height', show ? `${this.offsetHeight}px` : '0px');
        // Lets the mobile tab bar step aside while the buy bar is showing.
        if (show) root.setAttribute('data-sticky-atc', 'visible');
        else root.removeAttribute('data-sticky-atc');
      }).observe(target);
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------- Related products (loaded when near the viewport) ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.children.length || !this.dataset.url) return;
      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          this.load();
        },
        { rootMargin: '600px 0px' }
      );
      observer.observe(this);
    }

    async load() {
      try {
        const res = await fetch(this.dataset.url);
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
      } catch (_) {
        /* Related products are optional; fail quietly. */
      }
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Complete your order: this product plus ticked complementary products ---------- */
  class BundlePicker extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-product-section]');
      this.mainInput = this.section?.querySelector('[data-variant-id]');
      this.mainRow = this.querySelector('[data-bundle-main]');
      this.totalEl = this.querySelector('[data-bundle-total]');
      this.button = this.querySelector('[data-bundle-add]');
      this.errorEl = this.querySelector('[data-bundle-error]');

      this.addEventListener('change', (event) => {
        const select = event.target.closest('select');
        if (select) {
          const row = select.closest('[data-bundle-item]');
          const option = select.selectedOptions[0];
          row.dataset.variantId = option.value;
          row.dataset.price = option.dataset.price;
          const priceEl = row.querySelector('[data-bundle-price]');
          if (priceEl) priceEl.textContent = money(option.dataset.price);
        }
        this.update();
      });

      // Keep the main row in step with the variant picked above.
      this.section?.addEventListener('variant:change', (event) => {
        const v = event.detail.variant;
        if (!this.mainRow) return;
        this.mainRow.dataset.available = String(Boolean(v && v.available));
        if (v) {
          this.mainRow.dataset.price = v.price;
          const priceEl = this.mainRow.querySelector('[data-bundle-price]');
          if (priceEl) priceEl.textContent = money(v.price);
        }
        this.update();
      });

      this.button?.addEventListener('click', () => this.add());
      this.update();
    }

    selectedRows() {
      return [...this.querySelectorAll('[data-bundle-item]')].filter((row) => {
        const box = row.querySelector('input[type="checkbox"]');
        return !box || box.checked;
      });
    }

    update() {
      const rows = this.selectedRows();
      this.querySelectorAll('[data-bundle-item]').forEach((row) => row.classList.toggle('is-off', !rows.includes(row)));
      const total = rows.reduce((sum, row) => sum + Number(row.dataset.price || 0), 0);
      if (this.totalEl) this.totalEl.textContent = money(total);
      const mainAvailable = !this.mainRow || this.mainRow.dataset.available === 'true';
      // With every add-on unticked this is just the main button again, so step aside.
      if (this.button) this.button.disabled = !mainAvailable || rows.length < 2;
    }

    async add() {
      const Cart = Theme.Cart;
      const items = this.selectedRows()
        .map((row) => ({
          id: Number(row.hasAttribute('data-bundle-main') ? this.mainInput?.value : row.dataset.variantId),
          quantity: 1,
        }))
        .filter((item) => item.id);
      if (!Cart || items.length < 2 || this.button.getAttribute('aria-disabled') === 'true') return;

      this.button.classList.add('is-loading');
      this.button.setAttribute('aria-disabled', 'true');
      if (this.errorEl) this.errorEl.hidden = true;

      try {
        const res = await fetch(`${Theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: Cart.sectionIds(), sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw data;

        if (Theme.cartType === 'page') {
          window.location.href = Theme.routes.cart;
          return;
        }
        Cart.render(data.sections);
        await Cart.refreshCount();
        document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
        const drawer = document.getElementById('CartDrawer');
        if (drawer && typeof drawer.open === 'function') drawer.open(this.button);
        else window.location.href = Theme.routes.cart;
      } catch (data) {
        const message = Cart.errorMessage(data);
        if (this.errorEl) {
          this.errorEl.textContent = message;
          this.errorEl.hidden = false;
        } else {
          Theme.toast(message);
        }
      } finally {
        this.button.classList.remove('is-loading');
        this.button.removeAttribute('aria-disabled');
      }
    }
  }
  customElements.define('bundle-picker', BundlePicker);

  document.querySelectorAll('[data-product-section]').forEach(initProduct);
})();
